import useSWR, { mutate } from "swr";

import * as fetcher from "../fetcher";

export interface QuotationCustomer {
  partyId: string;
  firstName: string;
  lastName: string;
  businessName?: string;
  email: string;
  mobilePhone?: string;
  countryCode?: string;
  stateCode?: string;
  city?: string;
  addressLine1?: string;
  zipCode?: string;
  language?: string;
  preferredLocale?: string;
  isAddedBillAddress?: boolean;
  isAddedDeliveryAddress?: boolean;
  isBusinessCustomer?: boolean;
  isNewDeliveryAddress?: boolean;
}

export interface SearchQuotationResult {
  createdAt: string;
  cartData?: {
    customer: QuotationCustomer;
  };
  metaData?: {
    totalPrice: {
      priceExclTax: number;
      taxDetails: any[];
      Currency: string;
      priceInclTax: number;
      type: string;
      currencyCode: string;
    };
    imageUrls: string[];
    orderCreationDate: string;
    quotationExpiryDate: string;
    followUpDateWithCustomer: string;
    numberOfPackages: number;
    totalItemCount: number;
  };
  validTill?: string;
  quotationId: string;
  createdBy?: string;
  countryCode?: string;
  name?: string;
  buCode?: string;
  customerIdentifier?: {
    id: string;
    email: string;
  };
  lastModifiedAt?: string;
  updateHistory?: {
    updatedBy: string;
    details: string;
    updatedAt: {
      _seconds: number;
      _nanoseconds: number;
    };
  }[];
  status?: string;
}

export const createQuotation = async (
  opportunityId: number,
  quotationId: string,
  quotationTitle?: string
) => {
  await fetcher.opportunity.post(`/${opportunityId}/quotations`, {
    quotationId,
    quotationTitle,
  });
  mutate(`/opportunities/${opportunityId}`);
  mutate(`/opportunities/tasks?opportunityId=${opportunityId}`);
};

export const updateQuotation = async (
  opportunityId: number,
  oldQuotationId: string,
  quotationId: string,
  quotationTitle: string
) => {
  await fetcher.opportunity.remove(
    `/${opportunityId}/quotations/${oldQuotationId}`
  );

  await fetcher.opportunity.post(`/${opportunityId}/quotations`, {
    quotationId,
    quotationTitle,
  });
  mutate(`/opportunities/${opportunityId}`);
};

export const deleteQuotation = async (
  opportunityId?: number,
  quotationId?: string
) => {
  await fetcher.opportunity.remove(
    `/${opportunityId}/quotations/${quotationId}`
  );
  mutate(`/opportunities/${opportunityId}`);
  mutate(`/opportunities/tasks?opportunityId=${opportunityId}`);
};

export function useQuotationSearch(searchTerm?: string) {
  return useSWR<SearchQuotationResult>(
    searchTerm ? `/by-id/${searchTerm}` : null,
    (url) => fetcher.saljaQuotation.get(url),
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
}

export function useQuotation(searchTerm?: string) {
  return useSWR<SearchQuotationResult>(
    searchTerm ? `/by-id/${searchTerm}?includeCartData=true` : null,
    (url) => fetcher.saljaQuotation.get(url),
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
}

export function useQuotationSearchByCustomerId(customerId?: string) {
  return useSWR<SearchQuotationResult[]>(
    customerId ? `/by-customer-id/${customerId}` : null,
    (url) => fetcher.saljaQuotation.get(url),
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
}
